<template>
  <div id="client-dashboard">
    <div v-if="loading" class="fill-row-loading cp-state">
      <feather-icon icon="LoaderIcon" svgClasses="h-6 w-6" class="cp-spin" />
      <div>{{ t('loading') }}</div>
    </div>
    <div v-else-if="loadError" class="cp-state">
      <vs-alert color="danger" active="true">{{ loadError }}</vs-alert>
      <vs-button size="small" class="mt-4" @click="loadAll">{{ t('retry') }}</vs-button>
    </div>

    <!-- Même trame que le tableau de bord de l'ERP (views/DashboardAnalytics.vue) :
         colonne gauche 7/12 = commandes en cours + Destination ; colonne droite
         5/12 = cartes d'état, liste, carte à onglets. -->
    <template v-else>
    <div class="vx-row mt-4 ml-0 mr-4 mb-4" style="text-align: right"></div>
    <div class="vx-row">
      <div class="vx-col lg:w-7/12 w-full">
        <vx-card :title="titleLeft" class="mb-base">
          <div slot="no-body" class="mt-4 ml-4 mr-4 mb-4" v-if="current.length > 0">
            <!-- Signification des couleurs, comme le tableau de bord broker. -->
            <div class="cp-legend">
              <vs-chip class="ag-grid-cell-chips" color="#bcbcbc"><small>{{ t('awaiting_confirmation') }}</small></vs-chip>
              <vs-chip class="ag-grid-cell-chips" color="danger"><small>{{ t('legend_production') }}</small></vs-chip>
              <vs-chip class="ag-grid-cell-chips" color="success"><small>{{ t('legend_stock_ready') }}</small></vs-chip>
              <vs-chip class="ag-grid-cell-chips" color="#c2d1f0">
                <feather-icon icon="TruckIcon" svgClasses="h-4 w-4" class="mr-1" />
                <small>{{ t('legend_transport_searching') }}</small>
              </vs-chip>
              <vs-chip class="ag-grid-cell-chips" color="#A9CAB7">
                <feather-icon icon="TruckIcon" svgClasses="h-4 w-4" class="mr-1" />
                <small>{{ t('legend_transport_confirmed') }}</small>
              </vs-chip>
            </div>
            <vs-table :data="current" class="table-dark-inverted" search :max-items="1000">
              <template slot-scope="{ data }">
                <vs-tr :data="tr" :key="indextr" v-for="(tr, indextr) in data">
                  <vs-td>
                    <vs-chip class="ag-grid-cell-chips" :color="statusChipColor(tr)">
                      <span><small>{{ indextr + 1 }}.&nbsp;</small><b>
                          <router-link :to="orderRoute(tr.id)" class="text-inherit" style="text-decoration: underline;">#{{ tr.id }}</router-link> ⟶
                        </b>
                        <span v-if="isToday(loadingDate(tr))" class="cp-today"><i class="cp-today-dot"></i>{{ t('leaving_teiu_today') }}</span>
                        <span v-else-if="loadingDate(tr)">{{ t('leaving_teiu', { date: date(loadingDate(tr)) }) }}</span>
                        <span v-else-if="tr.date_delivery_desired">{{ date(tr.date_delivery_desired) }}</span>
                        <span v-else>{{ t('not_planned') }}</span>
                        ⟶
                        <big><b>{{ volume(tr.totals.volume) }}</b><small>&nbsp;m3</small></big>&nbsp;<small>({{ tr.availability === 'to_prepare' ? t('to_prepare') : t('ready') }})</small></span>
                    </vs-chip>
                    &nbsp;
                    <vs-chip class="ag-grid-cell-chips" color="">
                      <img height="20px" :src="flagSrc(tr.delivery && tr.delivery.country_code)" :alt="tr.delivery && tr.delivery.country_code" />&nbsp;&nbsp;
                      {{ tr.company }} &nbsp;
                      <font v-if="tr.delivery && tr.delivery.place">({{ tr.delivery.place }} {{ tr.delivery.postal_code }})</font>
                      <font v-else>({{ t('no_delivery_address') }})</font>
                    </vs-chip>
                    <vs-chip v-if="loadingDate(tr)" class="ag-grid-cell-chips" :color="tr.transport.carrier_assigned ? '#A9CAB7' : '#c2d1f0'">
                      <span>#{{ tr.transport.id }}</span>
                      &nbsp;
                      <feather-icon icon="TruckIcon" svgClasses="h-5 w-5" />
                    </vs-chip>
                    <br />
                  </vs-td>
                  <vs-td style="width: 10px">
                    <feather-icon icon="ChevronRightIcon" svgClasses="h-5 w-5" class="cursor-pointer" @click="openOrder(tr.id)" />
                  </vs-td>
                </vs-tr>
              </template>
            </vs-table>
          </div>
          <div v-else slot="no-body" class="mt-4 ml-4 mr-4 mb-4">
            <p class="text-center p-6" style="color: #888;">{{ t('no_current_orders') }}</p>
          </div>
        </vx-card>

        <!-- Carte open source (OpenFreeMap), affichée d'emblée : usine → livraisons. -->
        <vx-card :title="t('destination')" class="mb-base">
          <div slot="no-body" class="mt-2 ml-4 mr-4 mb-4">
            <client-delivery-map :orders="mapOrders" @open="openOrder" />
          </div>
        </vx-card>
      </div>

      <div class="vx-col lg:w-5/12 w-full">
        <!-- Liste sobre, comme « Live attendance » -->
        <vx-card :title="t('latest_invoices')" class="mb-base">
          <div slot="no-body" class="mt-0 ml-4 mr-4 mb-4">
            <div v-if="invoices.length > 0">
              <div v-for="inv in invoices.slice(0, 6)" :key="inv.id" class="flex items-center justify-between py-2"
                style="border-bottom: 1px solid #eee; cursor: pointer;" @click="go('client-invoice', inv.id)">
                <span>
                  <feather-icon icon="FileTextIcon" svgClasses="h-4 w-4 text-success" class="mr-2" />
                  <b>{{ inv.invoice_no }}</b>
                </span>
                <small style="color: #888;">{{ date(inv.date) }} · {{ amount(inv.totals.ttc, inv.totals.currency) }}</small>
              </div>
            </div>
            <p v-else class="py-2" style="color: #888;">{{ t('no_invoices') }}</p>
          </div>
        </vx-card>

        <!-- Deux cartes, comme « Future departures » de l'ERP (Chady 2026-11-04 : pas d'onglets). -->
        <vx-card :title="t('upcoming_departures')" class="mb-base">
          <div slot="no-body" class="mt-0 ml-4 mr-4 mb-4">
            <vs-table v-if="upcoming.length" :data="upcoming">
              <template slot-scope="{ data }">
                <tbody>
                  <vs-tr :key="d.id" v-for="d in data">
                    <vs-td>
                      <vs-chip class="ag-grid-cell-chips" color="#AAA">
                        <span>{{ d.id }}</span>
                        &nbsp;
                        <feather-icon icon="TruckIcon" svgClasses="h-5 w-5" />
                      </vs-chip>
                    </vs-td>
                    <vs-td>
                      <small :style="{ verticalAlign: 'top' }">{{ date(d.loading_date) }}</small>
                      <div v-if="d.truck_plate"><small style="color: #888;">{{ d.truck_plate }}</small></div>
                    </vs-td>
                    <vs-td>
                      <vs-chip class="ag-grid-cell-chips" color="">
                        <img height="20px" :src="flagSrc(d.country_code)" :alt="d.country_code" />
                        &nbsp;{{ d.place || d.company }}
                      </vs-chip>
                    </vs-td>
                    <vs-td>
                      <feather-icon icon="PackageIcon" svgClasses="h-5 w-5" :style="{ cursor: 'pointer' }" @click="openOrder(d.orders[0])" />
                    </vs-td>
                  </vs-tr>
                </tbody>
              </template>
            </vs-table>
            <p v-else class="p-4" style="color: #888;">{{ t('no_upcoming') }}</p>
          </div>
        </vx-card>

        <vx-card :title="t('on_the_road')" class="mb-8">
          <div slot="no-body" class="mt-0 ml-4 mr-4 mb-4">
            <div v-if="onTheRoad.length">
              <div v-for="d in onTheRoad" :key="d.id" class="cp-road">
                <div class="flex items-center justify-between">
                  <vs-chip class="ag-grid-cell-chips" color="#A9CAB7">
                    <span>{{ d.id }}</span>&nbsp;<feather-icon icon="TruckIcon" svgClasses="h-5 w-5" />
                  </vs-chip>
                  <vs-chip class="ag-grid-cell-chips" color="">
                    <img height="20px" :src="flagSrc(d.country_code)" :alt="d.country_code" />&nbsp;{{ d.place || d.company }}
                  </vs-chip>
                </div>
                <div class="flex items-center justify-between mt-2">
                  <span v-if="isToday(d.loading_date)" class="cp-today"><i class="cp-today-dot"></i>{{ t('today') }}</span>
                  <small v-else>{{ date(d.loading_date) }}</small>
                  <small v-if="d.delivery_date"><b>{{ t('estimated_delivery') }} : {{ date(d.delivery_date) }}</b></small>
                </div>
                <vs-progress :percent="d.progress" color="success" class="mt-1" />
                <div class="flex items-center justify-between">
                  <small style="color: #888;">
                    <template v-if="d.truck_plate">{{ d.truck_plate }}<template v-if="d.trailer_plate"> / {{ d.trailer_plate }}</template></template>
                  </small>
                  <small>
                    <span v-for="id in d.orders" :key="id" class="cp-link ml-2" @click="openOrder(id)">#{{ id }}</span>
                  </small>
                </div>
              </div>
            </div>
            <p v-else class="p-4" style="color: #888;">{{ t('no_on_the_road') }}</p>
          </div>
        </vx-card>
      </div>
    </div>
    </template>
  </div>
</template>

<script>
// Portail client → tableau de bord (page d'arrivée). Calqué sur le tableau de
// bord de l'ERP (views/DashboardAnalytics.vue), carte pour carte : commandes en
// cours en table à pastilles (prête / à préparer + m³, client et lieu, camion),
// Destination (carte open source affichée d'emblée), liste des dernières
// factures, prochains départs, camions sur la route.
// Jamais de quantité de stock : un signe par commande.
import portalPage from '@/views/client/portalPage.js'
import { portalGet } from '@/views/client/portalApi.js'
import ClientDeliveryMap from '@/views/client/ClientDeliveryMap.vue'
import { tripWindow, tripProgress } from '@/views/client/trip.js'

const FLAGS = require.context('@/assets/images/flags', false, /\.png$/)

export default {
  name: 'ClientDashboard',
  components: { ClientDeliveryMap },
  mixins: [portalPage],
  data () {
    return { all: [], invoices: [] }
  },
  computed: {
    // Même forme que l'ERP (« Orders in progress ⟶ x m3 / y m3 ordered (z%) ») : x = volume
    // des commandes prêtes du client, pas une quantité de stock.
    titleLeft () {
      const total = this.current.reduce((s, o) => s + (o.totals.volume || 0), 0)
      const ready = this.current.filter(o => o.availability !== 'to_prepare').reduce((s, o) => s + (o.totals.volume || 0), 0)
      const pct = total ? Math.round((ready / total) * 100) : 0
      return `${this.t('nav_orders')} ⟶ ${this.volume(ready)} m3 / ${this.volume(total)} m3 ${this.t('ordered')} (${pct}%)`
    },
    // Commandes en cours, par la date affichée : départ de Teiu dès qu'un transport
    // est daté, sinon date de livraison souhaitée.
    current () {
      const key = o => `${this.loadingDate(o) || o.date_delivery_desired || o.date || ''}#${String(o.id).padStart(10, '0')}`
      return this.all.filter(o => o.status === 'in_progress').sort((a, b) => key(a).localeCompare(key(b)))
    },
    today () {
      const d = new Date()
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
    },
    // Un camion par transport, toutes commandes confondues : une commande est
    // facturée au chargement, le camion en route porte donc des commandes facturées.
    transports () {
      const map = new Map()
      for (const o of this.all) {
        const tr = o.transport
        if (!tr || !tr.id || !tr.loading_date) continue
        if (!map.has(tr.id)) map.set(tr.id, Object.assign({}, tr, { orders: [], company: o.company, place: null, country_code: null, dest: null }))
        const entry = map.get(tr.id)
        entry.orders.push(o.id)
        // Destination : la première commande du camion qui a une adresse de livraison.
        if (!entry.place && o.delivery && o.delivery.place) {
          entry.place = o.delivery.place
          entry.country_code = o.delivery.country_code
        }
        if (!entry.dest && o.delivery && o.delivery.lat !== null && o.delivery.lng !== null) {
          entry.dest = { lat: o.delivery.lat, lng: o.delivery.lng }
        }
      }
      return Array.from(map.values())
    },
    // Chargement à venir (pas encore parti).
    upcoming () {
      return this.transports
        .filter(d => d.loading_date > this.today)
        .sort((a, b) => a.loading_date.localeCompare(b.loading_date))
    },
    // Chargé (aujourd'hui ou avant) et pas encore arrivé ; arrivée = livraison estimée de
    // l'ERP, sinon estimée d'après la distance (trip.js). Même avancement que le camion de la carte.
    onTheRoad () {
      const now = Date.now()
      return this.transports
        .filter(d => d.loading_date <= this.today && now < tripWindow(d, d.dest).end)
        .map(d => Object.assign({}, d, { progress: Math.max(5, tripProgress(d, d.dest, now)) }))
        .sort((a, b) => b.loading_date.localeCompare(a.loading_date))
    },
    // Carte : commandes en cours (dans l'ordre de la liste, pour que les numéros des
    // pastilles soient ceux de la table) puis celles dont le camion est sur la route.
    mapOrders () {
      const ids = new Set()
      for (const d of this.onTheRoad) d.orders.forEach(id => ids.add(id))
      return this.current.concat(this.all.filter(o => o.status !== 'in_progress' && ids.has(o.id)))
    }
  },
  methods: {
    // Couleurs de l'ERP : vert = prête, rouge = à préparer, gris = en attente de validation.
    statusChipColor (o) {
      if (o.awaiting_confirmation) return '#bcbcbc'
      return o.availability === 'to_prepare' ? 'danger' : 'success'
    },
    loadingDate (o) {
      return (o.transport && o.transport.loading_date) || null
    },
    volume (v) {
      return (Number(v) || 0).toFixed(2)
    },
    flagSrc (cc) {
      const code = String(cc || '').toUpperCase() === 'GB' ? 'UK' : String(cc || '').toUpperCase()
      try { return FLAGS(`./${code}.png`) } catch (e) { return FLAGS('./UE.png') }
    },
    orderRoute (id) {
      return { name: 'client-order', params: { id }, query: this.asQuery }
    },
    openOrder (id) {
      this.open('client-order', id)
    },
    go (name, id) {
      this.$router.push({ name, params: id ? { id } : {}, query: this.asQuery }).catch(() => {})
    },
    async loadAll () {
      this.loading = true
      this.loadError = null
      const as = this.portal.as
      // Une liste en échec n'empêche pas les autres de s'afficher.
      const [all, invoices] = await Promise.all([
        portalGet('/orders/all', as).catch(() => null),
        portalGet('/invoices', as).catch(() => null)
      ])
      if (!all && !invoices) this.loadError = this.t('load_error')
      this.all = (all && all.orders) || []
      this.invoices = (invoices && invoices.invoices) || []
      this.loading = false
    }
  },
  created () {
    this.loadAll()
  }
}
</script>

<style lang="scss">
/* Mêmes règles que DashboardAnalytics.vue (non scopées là-bas), limitées ici au portail. */
#client-dashboard {
  .cp-road {
    padding: 10px 4px 12px;
    border-bottom: 1px solid #eee;
  }
  .ag-grid-cell-chips {
    top: 12px;
    transform: translateY(-50%);
    line-height: 1;
  }
  .cp-legend {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    padding: 0 0 8px;
    .ag-grid-cell-chips {
      float: none;
      top: 0;
      transform: none;
    }
  }
}
</style>
